
/*
 * ----------------------------------------------------------------------------------
 * Imports - External
 * ----------------------------------------------------------------------------------
 */

import {
    CollaboratingGroupContext,
    CrfCondition,
    CrfForm,
    FormBreadcrumbs,
    GroupedFields,
    GroupedTableArrayField,
    ICrfConditionParameters,
    IGroupedTableArrayFieldColumn,
    Input,
    InstitutionContext,
    MasterGroupContext,
    PageLayout,
    PatientContext,
    RouteLoading,
    Select,
    Text,
    DatePicker,
    FileUpload,
    IFileUpload,
    usePatientSummary,
    FormContext,
    FormDefinitionsContext,
    ValidationErrorType,
    IFormValidationError,
    asyncDebounce,
    useSnackbar,
    FormSubmitButton,
    usePatientById,
    OnlinePatientManagementContext,
    EventDefinitionContext
} from '@ngt/opms';
import { Permission, usePermissionsByIds } from '@ngt/opms-bctapi';
import React, { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';


/*
 * ----------------------------------------------------------------------------------
 * Imports - Internal
 * ----------------------------------------------------------------------------------
 */
import { AlertTitle, Box, Button, Dialog, DialogContent, DialogContentText, styled, Typography } from '@mui/material';
import { ConditionMode, IFormSubmit, IFormValidate, useFormActions, useScopedField, ValidateOn } from '@ngt/forms-core';
import { AppSettings } from '../../../AppSettings';
import { useNavigate, useParams } from 'react-router-dom';
import * as Dtos from '../../../api/dtos';
import RadiologistSelector from '../fields/RadiologistSelector';

/*
 * ---------------------------------------------------------------------------------
 * constant
 * ---------------------------------------------------------------------------------
 */
const existingLink = (value?: IFileUpload) => {
    const file = value as Dtos.File;

    return `/opms/file/view/${file?.id}`;
}

const existingDownloadLink = (value?: IFileUpload) => {
    const file = value as Dtos.File;

    return `/opms/file/download/${file?.id}`;
}


/*
 * ----------------------------------------------------------------------------------
 * Interface
 * ----------------------------------------------------------------------------------
 */

interface ISystemErrorFormProps {}

interface IRegisterDialogProps {
    dialogOpen: boolean,
    toggleDialogOpen: () => void,
    systemErrorForm?: Dtos.SystemErrorForm
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */
const StyledSubheading = styled(Typography)(({ theme }) => ({
    padding: theme.spacing(3, 2, 3, 2)
    
}))

const StyledDialogTitle = styled(Typography)(({ theme }) => ({
    padding: theme.spacing(3),
    fontSize: '1.25rem',
    fontWeight: 500
}))


/*
 * ----------------------------------------------------------------------------------
 * Components
 * ----------------------------------------------------------------------------------
 */

const approvedBySpecify = ({ formState, parentFieldName }: ICrfConditionParameters<Dtos.SystemErrorForm>) =>  {
    const regex = /systemErrorInfo\[(?<Index>[0-9]*)]/;
    const res = regex.exec(parentFieldName ?? "");
    const idx = res?.groups?.['Index'];

    if (!idx) return false;

    const approvedBy = formState?.value?.systemErrorInfo?.[+idx]?.approvedBy;

    return approvedBy === Dtos.LookupSystemErrorApprovedByType.Other;
}

const systemErrorInfoNew = ({ formState, parentFieldName }: ICrfConditionParameters<Dtos.SystemErrorForm>) => {
    const regex = /systemErrorInfo\[(?<Index>[0-9]*)].(?<GroupedField>.*)/;
    const res = regex.exec(parentFieldName ?? "");
    const idx = res?.groups?.['Index'];

    if (!idx) return false;

    const id = formState?.value?.systemErrorInfo?.[+idx]?.id;

    return id == null;
}

const systemErrorSourceDocumentNew = ({ formState, parentFieldName }: ICrfConditionParameters<Dtos.SystemErrorForm>) => {
    const regex = /systemErrorSourceDocuments\[(?<Index>[0-9]*)].(?<GroupedField>.*)/;
    const res = regex.exec(parentFieldName ?? "");
    const idx = res?.groups?.['Index'];

    if (!idx) return false;

    const id = formState?.value?.systemErrorSourceDocuments?.[+idx]?.id;

    return id == null;
}

const useSystemErrorInfoColumns = (override: boolean): Array<IGroupedTableArrayFieldColumn<Dtos.SystemErrorInfo>> => {
    const columns = React.useMemo(() => {
        return [
            {
                propertyPath: 'eventErrorsByPassed',
                TableCellProps: {
                    sx: {
                        minWidth: 150,
                        maxWidth: 150,
                        width: 150
                    }
                },
                content: (
                    <Input
                        component={Select}
                        disabled
                    />
                )
            },
            {
                propertyPath: 'approvedBy',
                TableCellProps: {
                    sx: {
                        minWidth: 150,
                        maxWidth: 150,
                        width: 150
                    }
                },
                content: (
                    <>
                        {
                            override ? 
                                <CrfCondition
                                    mode={ConditionMode.Enable}
                                    condition={systemErrorInfoNew}
                                    subscription={{ value: true }}
                                >
                                    <Input
                                        component={Select}
                                    />
                                </CrfCondition>
                                :
                                <Input
                                    component={Select}
                                />
                        }
                    </>
                )
            },
            {
                propertyPath: 'approvedByOther',
                TableCellProps: {
                    sx: {
                        minWidth: 150,
                        maxWidth: 150,
                        width: 150
                    }
                },
                content: (
                    <>
                        {
                            override ? 
                                <CrfCondition
                                    mode={ConditionMode.Enable}
                                    condition={systemErrorInfoNew}
                                    subscription={{ value: true }}
                                >
                                    <CrfCondition
                                        mode={ConditionMode.Enable}
                                        condition={approvedBySpecify}
                                        subscription={{ value: true }}
                                    >
                                        <Input
                                            component={Text}
                                        />
                                    </CrfCondition>
                                </CrfCondition>
                                :
                                <CrfCondition
                                    mode={ConditionMode.Enable}
                                    condition={approvedBySpecify}
                                    subscription={{ value: true }}
                                >
                                    <Input
                                        component={Text}
                                    />
                                </CrfCondition>
                        }
                    </>
                    
                )
            },
            {
                propertyPath: 'dateSystemErrorByPassed',
                TableCellProps: {
                    sx: {
                        minWidth: 150,
                        maxWidth: 150,
                        width: 150
                    }
                },
                content: (
                    <>
                        {
                            override ? 
                                <CrfCondition
                                    mode={ConditionMode.Enable}
                                    condition={systemErrorInfoNew}
                                    subscription={{ value: true }}
                                >
                                    <Input
                                        component={DatePicker}
                                    />
                                </CrfCondition>
                                :
                                <Input
                                    component={DatePicker}
                                />
                        }
                    </>
                )
            },
            {
                propertyPath: 'reason',
                TableCellProps: {
                    sx: {
                        minWidth: 200,
                        maxWidth: 200,
                        width: 200
                    }
                },
                content: (
                    <>
                        {
                            override ? 
                                <CrfCondition
                                    mode={ConditionMode.Enable}
                                    condition={systemErrorInfoNew}
                                    subscription={{ value: true }}
                                >
                                    <Input
                                        component={Text}
                                        multiline
                                    />
                                </CrfCondition>
                                :
                                <Input
                                    component={Text}
                                    multiline
                                />
                        }
                    </>
                )
            },
        ];
    }, [override]);

    return columns;
};

const useSystemErrorSourceDocumentsColumns = (override: boolean): Array<IGroupedTableArrayFieldColumn<Dtos.SystemErrorSourceDocument>> => {
    const columns = React.useMemo(() => {
        return [
            {
                propertyPath: 'eventErrorsByPassed',
                TableCellProps: {
                    sx: {
                        minWidth: 250,
                        maxWidth: 250,
                        width: 250
                    }
                },
                content: (
                    <Input
                        component={Select}
                        disabled
                    />
                )
            },
            {
                propertyPath: 'name',
                TableCellProps: {
                    sx: {
                        minWidth: 250,
                        maxWidth: 250,
                        width: 250
                    }
                },
                content: (
                    <>
                        {
                            override ? 
                                <CrfCondition
                                    mode={ConditionMode.Enable}
                                    condition={systemErrorSourceDocumentNew}
                                    subscription={{ value: true }}
                                >
                                    <Input component={Text} />
                                </CrfCondition>
                                :
                                <Input component={Text} />
                        }
                    </>
                )
            }, 
            {
                propertyPath: 'fileTypeId',
                TableCellProps: {
                    sx: {
                        minWidth: 250,
                        maxWidth: 250,
                        width: 250
                    }
                },
                content: (
                    <>
                        {
                            override ? 
                                <CrfCondition
                                    mode={ConditionMode.Enable}
                                    condition={systemErrorSourceDocumentNew}
                                    subscription={{ value: true }}
                                >
                                    <Input component={Select} />
                                </CrfCondition>
                                :
                                <Input component={Select} />
                        }
                    </>
                )
            },
            {
                propertyPath: 'file',
                content: (
                    <>
                        {
                            override ? 
                                <CrfCondition
                                    mode={ConditionMode.Enable}
                                    condition={systemErrorSourceDocumentNew}
                                    subscription={{ value: true }}
                                >
                                    <Input
                                        component={FileUpload}
                                        existingLink={existingLink}
                                        existingDownloadLink={existingDownloadLink}
                                    />
                                </CrfCondition>
                                :
                                <Input
                                    component={FileUpload}
                                    existingLink={existingLink}
                                    existingDownloadLink={existingDownloadLink}
                                />
                        }
                    </>
                )
            }
        ];
    }, [override]);

    return columns;
};


const SystemErrorDetailsColumns: Array<IGroupedTableArrayFieldColumn<Dtos.SystemErrorDetails>> = [
    {
        propertyPath: 'eventErrorsByPassed',
        TableCellProps: {
            sx: {
                minWidth: 250,
                maxWidth: 250,
                width: 250
            }
        },
        content: (
            <Input
                component={Select}
                disabled
            />
        )
    },
    {
        propertyPath: 'formName',
        TableCellProps: {
            sx: {
                minWidth: 250,
                maxWidth: 250,
                width: 250
            }
        },
        content: (
            <Input
                component={Text}
                multiline
                disabled
            />
        )
    },
    {
        propertyPath: 'fieldName',
        TableCellProps: {
            sx: {
                minWidth: 300,
                maxWidth: 300,
                width: 300
            }
        },
        content: (
            <Input
                component={Text}
                multiline
                disabled
            />
        )
    },
    {
        propertyPath: 'details',
        content: (
            <Input
                component={Text}
                multiline
                disabled
            />
        )
    },
];

const RegisterDialog: FC<IRegisterDialogProps> = ({
    dialogOpen,
    toggleDialogOpen
}) => {
    //const { loading: formLoading, validating: formValidating, mutate } = useContext(FormContext);

    //const [formSaving, setFormSaving] = useState(false);

    const { state: { value: selectedRadiologist }, context: { setValue: setRadiologistValue } } = useScopedField('registrationRadiologist', { value: true });

    const onDialogClose = useCallback(() => {
        toggleDialogOpen();
        setRadiologistValue(undefined);
    }, [toggleDialogOpen, setRadiologistValue]);

    return (
        <>
            <Dialog
                open={dialogOpen}
                onClose={toggleDialogOpen}
                aria-labelledby="review-dialog-title"
                aria-describedby="review-dialog-description"
                maxWidth="sm"
                fullWidth
            >
                <StyledDialogTitle
                    color="secondary"
                >
                    Register
                </StyledDialogTitle>

                <DialogContent 
                    style={{ paddingTop: 0 }}
                >
                    <DialogContentText>
                        Radiologist review process will start when the patient is registered.
                    </DialogContentText>

                    <DialogContentText >
                        Please select a radiologist. The selected radiologist will receive an email notification about the review.
                    </DialogContentText>
                </DialogContent>

                <>
                    <RadiologistSelector 
                        dialog={true}
                        propertyPath="registrationRadiologist"
                    />

                    <Box
                        display="flex"
                        justifyContent="flex-end"
                        gap={2}
                        padding={2}
                    >
                        <Button
                            color="secondary"
                            onClick={onDialogClose}
                            
                        >
                            Cancel
                        </Button>

                        <FormSubmitButton
                            color="primary"   
                            variant="contained"      
                            disabled={!selectedRadiologist}                          
                        >
                            Register 
                        </FormSubmitButton>
                    </Box>
                </>
            </Dialog>
        </>
    )
}

const SystemErrorFields = () => {
    const { data: patient } = useContext(PatientContext);
    const { data: form } = React.useContext(FormContext);
    const { data: formDefinitions } = useContext(FormDefinitionsContext);

    const typedForm = form as Dtos.SystemErrorForm;

    const params = useParams();

    const override = !!params.override;

    const { data: patientSummary, validating: patientSummaryValidating, loading: patientSummaryLoading } = usePatientSummary(patient?.id);

    const {setFieldValue} = useFormActions();

    const getSystemOverrideEvent = React.useCallback(() => {
        switch (patient?.patientStateId) {
            case Dtos.PatientStateType.Screening:
                return Dtos.LookupEventErrorsByPassedType.Screening;
            case Dtos.PatientStateType.ConsentPending:
            case Dtos.PatientStateType.Consent:
                return Dtos.LookupEventErrorsByPassedType.Consent;
            case Dtos.PatientStateType.Registration:
                return Dtos.LookupEventErrorsByPassedType.Registration;
            case Dtos.PatientStateType.RadiologistReview:
                return Dtos.LookupEventErrorsByPassedType.RadiologistReview;
            case Dtos.PatientStateType.Allocation:
                return Dtos.LookupEventErrorsByPassedType.Allocation;
            case Dtos.PatientStateType.CorrectionRequired:
                return Dtos.LookupEventErrorsByPassedType.Allocation;
            default:
                return undefined;
        }
    }, [patient]);

    const getNewPatientStatus = React.useCallback(() => {
        switch (patient?.patientStateId) {
            case Dtos.PatientStateType.Screening:
                return Dtos.PatientStateType.ConsentPending;
            case Dtos.PatientStateType.ConsentPending:
            case Dtos.PatientStateType.Consent:
                return Dtos.PatientStateType.Registration;
            case Dtos.PatientStateType.Registration:
                return Dtos.PatientStateType.RadiologistReview;
            case Dtos.PatientStateType.RadiologistReview:
                return Dtos.PatientStateType.Allocation;
            case Dtos.PatientStateType.Allocation:
                return Dtos.PatientStateType.Allocated;
            case Dtos.PatientStateType.CorrectionRequired:
                return Dtos.PatientStateType.Allocated;
        }
    }, [patient]);

    React.useEffect(() => {
        if (override) {
            setFieldValue(`newPatientStatus`, getNewPatientStatus());

            var systemOverrideEvent = getSystemOverrideEvent();

            let systemErrorInfo = {
                eventErrorsByPassed: systemOverrideEvent
            } as Partial<Dtos.SystemErrorDetails>;

            setFieldValue(`systemErrorInfo`, [...(typedForm?.systemErrorInfo ?? []), systemErrorInfo]);

            var validationForms = patientSummary?.events?.map(e => e.forms)?.reduce((a, b) => a?.concat(b ?? []), []) ?? []
            
            var validationFormErrors = validationForms.filter(form => (form.formValidation?.errors?.length ?? 0) > 0 && form.formDefinition?.id !== Dtos.FormDefinitionType.SystemErrorForm)
                .map(form => {

                    const formDefinition = formDefinitions?.find(fd => fd.id === form.formDefinition?.id);

                    return form.formValidation?.errors?.map(error => {
                        let systemErrorDetails: Dtos.SystemErrorDetails = {} as Dtos.SystemErrorDetails;

                        let fieldName = "";

                        if (!error.property.match(/\[\d\]\./g)) {
                            fieldName = formDefinition?.properties?.find(p => p.propertyName === error.property)?.label ?? "";
                        } else {
                            let subformPropertyName = error?.property.split(/\[\d\]\./g)[0];
                            let propertyName = error?.property.split(/\[\d\]\./g)[1];

                            fieldName = formDefinition?.subformProperties[subformPropertyName].find(p => p.propertyName === propertyName)?.label ?? "";
                        }

                        systemErrorDetails.eventErrorsByPassed = systemOverrideEvent;
                        systemErrorDetails.formName = (formDefinition?.name ?? "").replace(/<.*?>/g, "");
                        systemErrorDetails.fieldName = fieldName.replace(/<.*?>/g, "");
                        systemErrorDetails.details = error?.detailedMessage.replace(/<.*?>/g, "");

                        return systemErrorDetails;
                    })
                })
                .reduce((a, b) => a?.concat(b ?? []), []) ?? [];

            var newValidationFormErrors = validationFormErrors.filter(error => !typedForm?.systemErrorDetails?.find(x => x.eventErrorsByPassed === error.eventErrorsByPassed && x.formName === error.formName && x.fieldName === error.fieldName && x.details === error.details))

            setFieldValue(`systemErrorDetails`, [...(typedForm?.systemErrorDetails ?? []), ...newValidationFormErrors]);

            //console.log(validationFormErrors);
        }
    }, [patientSummary, override, patient, setFieldValue, form, formDefinitions, getSystemOverrideEvent]);

    const onSourceDocumentAdd = useCallback(() => {
        return {
            eventErrorsByPassed: getSystemOverrideEvent()
        } as Dtos.SystemErrorSourceDocument
    }, [getSystemOverrideEvent])

    const systemErrorInfoColumns = useSystemErrorInfoColumns(override);
    const systemErrorSourceDocumentsColumns = useSystemErrorSourceDocumentsColumns(override);

    if (patientSummaryValidating || patientSummaryLoading) {
        return <RouteLoading />
    }

    return (
        <>
            <GroupedFields>
                <StyledSubheading
                    variant="h2"
                >
                    System Error Overrides
                </StyledSubheading>
                <GroupedTableArrayField
                    propertyPath="systemErrorInfo"
                    columns={systemErrorInfoColumns}
                    entityName="System Error Override"
                    AddButtonProps={{
                        sx: {
                            mb: 2
                        }
                    }}
                    hideDeletion
                    hideAddButton
                />
            </GroupedFields>

            <GroupedFields>
                <StyledSubheading
                    variant="h2"
                >
                    System Error Details
                </StyledSubheading>
                <GroupedTableArrayField
                    propertyPath="systemErrorDetails"
                    columns={SystemErrorDetailsColumns}
                    entityName="Details"
                    AddButtonProps={{
                        sx: {
                            mb: 2
                        }
                    }}
                    hideDeletion
                    hideAddButton
                />
            </GroupedFields>
            
            <GroupedFields>
                <StyledSubheading
                    variant="h2"
                >
                    Source Documents
                </StyledSubheading>
                <GroupedTableArrayField
                    propertyPath="systemErrorSourceDocuments"
                    columns={systemErrorSourceDocumentsColumns}
                    entityName="Document"
                    AddButtonProps={{
                        sx: {
                            mb: 2
                        }
                    }}
                    createNewItem={onSourceDocumentAdd}
                    
                />
            </GroupedFields>
        </>
    )
}

const SystemErrorForm: React.FunctionComponent<ISystemErrorFormProps> = () => {
    const { data: masterGroup } = useContext(MasterGroupContext);
    const { data: collaboratingGroup } = useContext(CollaboratingGroupContext);
    const { data: institution } = useContext(InstitutionContext);
    const { data: eventDefinition } = useContext(EventDefinitionContext);
    const { data: patient, refresh: patientRefresh } = useContext(PatientContext);

    const {data: patientById, refresh: patientByIdRefresh} = usePatientById(patient?.id);
    const [ submitted, setSubmitted ] = useState(false);

    const {
        permissions: [
            canBypassErrors
        ],
        loading: permissionsLoading,
        
    } = usePermissionsByIds(
        [
            Permission.OpmsBypassErrors
        ], 
        masterGroup?.id, 
        collaboratingGroup?.id, 
        institution?.id, 
        patient?.id
    );

    const params = useParams();

    const override = !!params.override;

    const navigate = useNavigate();

    useEffect(() => {
        if(override && canBypassErrors === false && !permissionsLoading) {
            navigate(`/registration/${institution?.code}/${patient?.studyNumber}/staff/1/system-error-form/1`);
        }
    }, [override, canBypassErrors, patient, institution, navigate, permissionsLoading]);

    const systemErrorFormValidate: IFormValidate<Dtos.SystemErrorForm, IFormValidationError> = useCallback(async (formState) => {

        let errors: Record<string, IFormValidationError[]> = {};

        if ((formState.value?.systemErrorInfo?.length ?? 0) > 0) {

            formState.value?.systemErrorInfo.forEach((x, index) => {
                if(x.approvedBy === undefined) {
                    errors[`systemErrorInfo[${index}].approvedBy`] = [
                        {
                            property: 'systemErrorInfo.approvedBy',
                            code: 'SEI-001',
                            message: 'This field is required.',
                            detailedMessage: 'This field is required.',
                            type: ValidationErrorType.Critical
                        }
                    ];
                }

                if(x.approvedBy === Dtos.LookupSystemErrorApprovedByType.Other && x.approvedByOther === undefined) {
                    errors[`systemErrorInfo[${index}].approvedByOther`] = [
                        {
                            property: 'systemErrorInfo.approvedByOther',
                            code: 'SEI-002',
                            message: 'This field is required.',
                            detailedMessage: 'This field is required.',
                            type: ValidationErrorType.Critical
                        }
                    ];
                }

                if(x.dateSystemErrorByPassed === undefined) {
                    errors[`systemErrorInfo[${index}].dateSystemErrorByPassed`] = [
                        {
                            property: 'systemErrorInfo.dateSystemErrorByPassed',
                            code: 'SEI-003',
                            message: 'This field is required.',
                            detailedMessage: 'This field is required.',
                            type: ValidationErrorType.Critical
                        }
                    ];
                }

                if(x.reason === undefined) {
                    errors[`systemErrorInfo[${index}].reason`] = [
                        {
                            property: 'systemErrorInfo.reason',
                            code: 'SEI-004',
                            message: 'This field is required.',
                            detailedMessage: 'This field is required.',
                            type: ValidationErrorType.Critical
                        }
                    ];
                }
            })
        }

        if ((formState.value?.systemErrorSourceDocuments?.length ?? 0) > 0) {

            formState.value?.systemErrorSourceDocuments.forEach((x, index) => {
                if(x.name === undefined) {
                    errors[`systemErrorSourceDocuments[${index}].name`] = [
                        {
                            property: 'systemErrorSourceDocuments.name',
                            code: 'SESD-001',
                            message: 'This field is required.',
                            detailedMessage: 'This field is required.',
                            type: ValidationErrorType.Critical
                        }
                    ];
                }

                if(x.fileTypeId === undefined) {
                    errors[`systemErrorSourceDocuments[${index}].fileTypeId`] = [
                        {
                            property: 'systemErrorSourceDocuments.fileTypeId',
                            code: 'SESD-002',
                            message: 'This field is required.',
                            detailedMessage: 'This field is required.',
                            type: ValidationErrorType.Critical
                        }
                    ];
                }

                if(x.file === undefined) {
                    errors[`systemErrorSourceDocuments[${index}].file`] = [
                        {
                            property: 'systemErrorSourceDocuments.file',
                            code: 'SESD-003',
                            message: 'This field is required.',
                            detailedMessage: 'This field is required.',
                            type: ValidationErrorType.Critical
                        }
                    ];
                }
            })
        }

        return errors;
    }, []);

    const debouncedValidate = useMemo(() => {
        return asyncDebounce(systemErrorFormValidate, 500)
    }, [systemErrorFormValidate]);

    const { enqueueSnackbar } = useSnackbar();

    const getSubmitButtonText = React.useCallback(() => {
        if (!override) {
            return undefined
        } 

        switch (patient?.patientStateId) {
            case Dtos.PatientStateType.Screening:
                return "Complete Screening";
            case Dtos.PatientStateType.ConsentPending:
            case Dtos.PatientStateType.Consent:
                return "Complete Consent";
            case Dtos.PatientStateType.Registration:
                return "Register";
            case Dtos.PatientStateType.RadiologistReview:
                return "Complete Radiologist Review";
            case Dtos.PatientStateType.Allocation:
                return "Allocate";
            case Dtos.PatientStateType.CorrectionRequired:
                return "Allocate";
        }
    }, [override, patient]);

    const afterFormSave = useCallback(async () => {
        if(override)
        {
            await patientByIdRefresh();

            await patientRefresh();
            
            enqueueSnackbar(
                <>
                    <AlertTitle>
                        Participant Status Updated
                    </AlertTitle>
                    The participant status was successfully updated.
                </>,
                { variant: 'success' }
            );

            setSubmitted(true);
        }
    }, [institution, navigate, override, enqueueSnackbar, patientByIdRefresh, setSubmitted, patientRefresh]);

    useEffect(() => {
        if (submitted && patientById?.studyNumber) {
            navigate(`/registration/${institution?.code}/${patientById?.studyNumber}`);
        }
    }, [submitted, patientById, institution])

    const [registerDialogOpen, setRegisterDialogOpen] = useState<boolean>(false);

    const toggleRegisterDialog = useCallback(() => {
        setRegisterDialogOpen(!registerDialogOpen)
    }, [registerDialogOpen, setRegisterDialogOpen]);

    const { serviceStackClient: client } = useContext(OnlinePatientManagementContext);

    const onRegisterSubmit: IFormSubmit<Dtos.SystemErrorForm, IFormValidationError> = useCallback(async (form) => {
        if(!form.value?.registrationRadiologist) {
            setRegisterDialogOpen(true);
        } else {
            if (!form.value) {
                throw new Error("Form data is missing.");
            }

            try {
                await client.post(
                    new Dtos.SystemErrorFormPostSaveWithIds({
                        institutionId: institution?.id,
                        patientId: patient?.id,
                        eventDefinitionId: eventDefinition?.id,
                        eventRepeat: 1,
                        formRepeat: 1,
                        form: {...form.value},
                        createEvent: true
                    })
                );

                await afterFormSave();

                toggleRegisterDialog();
            }
            catch (error: any) {

                enqueueSnackbar(
                    <>
                        <AlertTitle>
                            Form Not Saved
                        </AlertTitle>
                        An error occurred while attempting to save the form.
                    </>,
                    { variant: 'critical' }
                );
            }
        }
    }, [setRegisterDialogOpen, enqueueSnackbar, client, toggleRegisterDialog, afterFormSave, patient, institution, eventDefinition]);

    if (permissionsLoading) {
        return (
            <RouteLoading />
        );
    }
    
    return (
        <PageLayout
            breadcrumbs={<FormBreadcrumbs />}
        >
            <CrfForm
                validateOn={ValidateOn.onChange}
                readOnly={!canBypassErrors}
                debug={AppSettings.debug}
                onValidate={debouncedValidate}
                afterFormSave={afterFormSave}
                onSubmit={override && patient?.patientStateId === Dtos.PatientStateType.Registration ? onRegisterSubmit : undefined}
                submitButtonText={getSubmitButtonText()}
                hideSubmitAndReturn={override}
            >
                <SystemErrorFields />

                <RegisterDialog 
                    dialogOpen={registerDialogOpen}
                    toggleDialogOpen={toggleRegisterDialog}
                />
            </CrfForm>
        </PageLayout>
    );
}


/*
 * ----------------------------------------------------------------------------------
 * Default Export
 * ----------------------------------------------------------------------------------
 */

export default SystemErrorForm;
